import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";

import { EmptyState } from "@/components/dashboard/data-table/empty-state";
import { FilterBar } from "@/components/dashboard/data-table/filter-bar";
import { Pagination } from "@/components/dashboard/data-table/pagination";
import { SortHeader } from "@/components/dashboard/data-table/sort-header";
import {
  TBody,
  TD,
  TEmpty,
  TH,
  THead,
  TR,
  Table,
  TableShell,
} from "@/components/dashboard/data-table/table";
import { PageHeader } from "@/components/dashboard/page-header";
import { QueryState } from "@/components/dashboard/query-state";
import { StatusPill } from "@/components/dashboard/status-pill";
import { PersonAvatar } from "@/components/employees/person-avatar";
import { breaks as copy, nav, status } from "@/content";
import { can, scopeToSupervisorId } from "@/constants/permissions";
import {
  buildQuery,
  readEnum,
  readNumber,
  readPage,
  readPageSize,
  readSort,
  emptyPage,
} from "@/utils/query-params";
import {
  BREAK_SORT_COLUMNS,
  BREAK_STATUSES,
  breakStatusCounts,
  listBreakGuards,
  listBreakRequests,
  listBreakTypes,
} from "@/api/breaks";
import { cn } from "@/utils";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useQueryParams } from "@/hooks/shared/useQueryParams";
import { useFmt } from "@/hooks/shared/useFmt";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";
import { useSession } from "@/context/UserContext";

const BASE_PATH = "/dashboard/breaks";

export const Route = createFileRoute("/_main/dashboard/breaks/")({
  component: () => (
    <RequirePermission action="leave.read">
      <BreaksPage />
    </RequirePermission>
  ),
});

function BreaksPage() {
  useDocumentTitle(copy.title);
  const { user } = useSession();
  const params = useQueryParams();
  const fmt = useFmt();
  const scope = scopeToSupervisorId(user);

  const sort = readSort(params, BREAK_SORT_COLUMNS, "createdAt");
  const activeStatus = readEnum(params, "status", BREAK_STATUSES);

  const breakTypes = useQuery({
    queryKey: ["breakTypes", "list"],
    queryFn: listBreakTypes,
    staleTime: 30 * 60 * 1000,
  });

  const query = {
    status: activeStatus,
    breakTypeId: readNumber(params, "breakType"),
    guardId: readNumber(params, "guard"),
    scopeToSupervisorId: scope,
    sort: { column: sort.column, direction: sort.direction },
    page: readPage(params),
    pageSize: readPageSize(params),
  };

  const list = useQuery({
    queryKey: ["breaks", "list", query],
    queryFn: () => listBreakRequests(query),
    placeholderData: keepPreviousData,
  });

  const countsQuery = useQuery({
    queryKey: ["breaks", "counts"],
    queryFn: breakStatusCounts,
  });
  const guardsQuery = useQuery({
    queryKey: ["breaks", "guards"],
    queryFn: listBreakGuards,
    staleTime: 5 * 60_000,
  });

  const result = list.data ?? emptyPage<never>();
  const counts = countsQuery.data ?? { pending: 0, approved: 0, rejected: 0 };
  const guards = guardsQuery.data ?? [];

  const canDecide = can(user.role, "leave.decide");
  const isFiltered = Boolean(params.breakType || params.guard);

  const tabs = [
    {
      value: undefined,
      label: copy.tabAll,
      count: counts.pending + counts.approved + counts.rejected,
    },
    { value: "pending" as const, label: copy.tabPending, count: counts.pending },
    { value: "approved" as const, label: copy.tabApproved, count: counts.approved },
    { value: "rejected" as const, label: copy.tabRejected, count: counts.rejected },
  ];

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader eyebrow={nav.groups.operations} title={copy.title} lead={copy.lead} />

      {!canDecide ? <p className="mt-5 text-xs text-n6">{copy.readOnlyNote}</p> : null}

      <div className="mt-6 flex flex-wrap gap-1 border-b border-n3">
        {tabs.map((tab) => {
          const active = activeStatus === tab.value;
          return (
            <Link
              key={tab.label}
              to={`${BASE_PATH}${buildQuery(params, { status: tab.value ?? null })}`}
              aria-current={active ? "page" : undefined}
              className={cn(
                "-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-xs font-semibold transition-colors duration-200 ease-brand",
                active ? "border-orange text-n9" : "border-transparent text-n6 hover:text-n8",
              )}
            >
              {tab.label}
              <span className="tabular text-[11px] text-n6">{fmt.count(tab.count)}</span>
            </Link>
          );
        })}
      </div>

      <div className="mt-5">
        <FilterBar
          params={params}
          basePath={BASE_PATH}
          selects={[
            {
              key: "breakType",
              label: copy.filterType,
              value: typeof params.breakType === "string" ? params.breakType : "",
              options: (breakTypes.data ?? []).map((type) => ({
                value: String(type.id),
                label: type.nameAr,
              })),
            },
            {
              key: "guard",
              label: copy.filterGuard,
              value: typeof params.guard === "string" ? params.guard : "",
              options: guards.map((guard) => ({ value: String(guard.id), label: guard.name })),
            },
          ]}
        />
      </div>

      <QueryState
        isPending={list.isPending}
        isError={list.isError && !list.data}
        error={list.error}
        onRetry={() => {
          void list.refetch();
        }}
      >
        <TableShell className="mt-5" busy={list.isPlaceholderData}>
          <Table>
            <THead>
              <SortHeader
                column="guard"
                label={copy.columnGuard}
                params={params}
                basePath={BASE_PATH}
                current={sort}
              />
              <TH hideBelow="lg">{copy.columnType}</TH>
              <SortHeader
                column="startAt"
                label={copy.columnWindow}
                params={params}
                basePath={BASE_PATH}
                current={sort}
              />
              <TH hideBelow="md" numeric>
                {copy.columnMinutes}
              </TH>
              <SortHeader
                column="createdAt"
                label={copy.columnFiled}
                params={params}
                basePath={BASE_PATH}
                current={sort}
              />
              <SortHeader
                column="status"
                label={copy.columnStatus}
                params={params}
                basePath={BASE_PATH}
                current={sort}
              />
            </THead>

            <TBody>
              {result.rows.length === 0 ? (
                <TEmpty colSpan={6}>
                  <EmptyState
                    variant={isFiltered ? "filtered" : "empty"}
                    title={isFiltered ? copy.emptyFilteredTitle : copy.emptyTitle}
                    lead={isFiltered ? copy.emptyFilteredLead : copy.emptyLead}
                    actionHref={isFiltered ? BASE_PATH : undefined}
                    actionLabel={isFiltered ? copy.clearFilters : undefined}
                  />
                </TEmpty>
              ) : (
                result.rows.map((row) => (
                  <TR key={row.id}>
                    <TD>
                      <div className="flex items-center gap-2.5">
                        <PersonAvatar name={row.guardName} photoUrl={row.photoUrl} />
                        <div className="min-w-0">
                          <Link
                            to={`${BASE_PATH}/${row.id}`}
                            className="font-semibold text-n9 transition-colors duration-200 ease-brand hover:text-orange-700"
                          >
                            {row.guardName}
                          </Link>
                          {row.supervisorName ? (
                            <span className="mt-0.5 block text-xs text-n6">{row.supervisorName}</span>
                          ) : null}
                        </div>
                      </div>
                    </TD>

                    <TD hideBelow="lg">
                      <span className="text-xs font-semibold text-n7">{row.breakType.nameAr}</span>
                      <span className="mt-0.5 block text-[11px] text-n6">
                        {row.breakType.countsAsWorked
                          ? copy.countsAsWorked
                          : copy.doesNotCountAsWorked}
                      </span>
                    </TD>

                    <TD>
                      <span className="tabular text-xs">
                        {row.startAt && row.endAt
                          ? fmt.range(row.startAt, row.endAt)
                          : row.status === "pending"
                            ? copy.startsOnApproval
                            : copy.notStarted}
                      </span>
                      {row.fromNow && row.startAt ? (
                        <span className="mt-0.5 block text-[11px] font-semibold text-orange-700">
                          {copy.fromNow}
                        </span>
                      ) : null}
                    </TD>

                    <TD hideBelow="md" numeric>
                      <span className="tabular text-xs">{fmt.count(row.minutes)}</span>
                    </TD>

                    <TD>
                      <span className="text-xs">{fmt.dateNumeric(row.createdAt)}</span>
                    </TD>

                    <TD>
                      {row.status === "pending" ? (
                        <StatusPill tone="attention">{status.pending}</StatusPill>
                      ) : row.status === "approved" ? (
                        <StatusPill tone="positive">{status.approved}</StatusPill>
                      ) : (
                        <StatusPill tone="negative">{status.rejected}</StatusPill>
                      )}
                      {row.reopenedAt ? (
                        <span className="mt-1 block text-[10px] font-semibold text-n6">
                          {copy.timelineReopened}
                        </span>
                      ) : null}
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>

          {result.rows.length > 0 ? (
            <Pagination
              page={result.page}
              pageCount={result.pageCount}
              pageSize={result.pageSize}
              total={result.total}
              params={params}
              basePath={BASE_PATH}
              numerals={user.numeralSystem}
            />
          ) : null}
        </TableShell>
      </QueryState>
    </div>
  );
}
